import {useCallback, useState} from 'react';

import {AddressFormV2Values} from 'presentation/components/ChangeData/Address/AddressFormV2/types';
import {ProfileEnum} from 'presentation/constants';
import {useMediaQueries} from 'presentation/pages';
import {useChangeDataFormStateStore} from 'presentation/pages/private/profile/store/changeDataFormStateStore';
import {useLocation, useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {formatAddress} from 'presentation/utils';
import {PredefinedFavoritePlacesEnum, PrincipalFavoritePlaceEnum} from 'domain/usecases';
import {useGetFavoriteAddressesQuery} from '../queries/useGetFavoriteAddressesQuery';
import {patchFavoriteAddress} from '../services/patchFavoriteAddress';
import {favoritePlaceToAddressFormParser} from '../utils/address';
import {FavoriteAddressesItem} from '../types';

function buildDowngradePayload(casaItem: FavoriteAddressesItem): AddressFormV2Values {
	const {address} = casaItem;
	return {
		favoriteAddress: PredefinedFavoritePlacesEnum.OTHER,
		address: address.nmLogradouro,
		number: String(address.nuEndereco),
		district: address.nmBairro,
		city: address.nmCidade,
		state: address.nmUf,
		zipCode: address.nuCep,
		complement: address.dsComplemento ?? '',
		withoutNumber: !address.nuEndereco,
		isMainAddress: false,
	};
}

export function useEditFavoriteAddress() {
	const [openEditFavoriteAddressError, setOpenEditFavoriteAddressError] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const {isMobile, isTablet} = useMediaQueries();
	const navigate = useNavigate();
	const {state: routeState} = useLocation();
	const setChangeDataFormState = useChangeDataFormStateStore(state => state.updateFormState);
	const queryClient = useQueryClient();

	const {data: favoriteAddresses} = useGetFavoriteAddressesQuery();

	const currentItemSequence =
		routeState?.item?.address?.sequence ??
		routeState?.item?.address?.cdBeneficiarioEnderecoFavorito;

	const isEditingCasaAddress =
		routeState?.item?.address?.flPrincipal === PrincipalFavoritePlaceEnum.YES;

	const currentCasaAddress =
		favoriteAddresses?.find(
			item =>
				item.address.flPrincipal === PrincipalFavoritePlaceEnum.YES &&
				(item.address.sequence ?? item.address.cdBeneficiarioEnderecoFavorito) !==
					Number(currentItemSequence),
		) ?? null;

	const hasCasaAddress = !isEditingCasaAddress && !!currentCasaAddress;

	const handleBack = () => {
		setChangeDataFormState({
			isFormActive: false,
			isFormDirty: false,
		});
		navigate(routeState?.from || ProfileEnum.FAVORITE_ADDRESSES_LIST);
	};

	const handleOpenEditFavoriteAddressError = () => setOpenEditFavoriteAddressError(true);
	const handleCloseEditFavoriteAddressError = () => setOpenEditFavoriteAddressError(false);

	const submitFavoriteAddress = useCallback(
		async (data: AddressFormV2Values) => {
			const isCasaSelection = data.favoriteAddress === PredefinedFavoritePlacesEnum.HOUSE;
			const finalData = {...data, isMainAddress: isCasaSelection};

			setIsSubmitting(true);
			try {
				await patchFavoriteAddress({
					cdBeneficiarioEnderecoFavorito: Number(currentItemSequence),
					sequence: Number(currentItemSequence),
					data: finalData,
					existingLatLng: routeState?.item?.address?.cdLatitude
						? {
								latitude: routeState.item.address.cdLatitude,
								longitude: routeState.item.address.cdLongitude,
							}
						: undefined,
				});

				if (isCasaSelection && currentCasaAddress) {
					await patchFavoriteAddress({
						cdBeneficiarioEnderecoFavorito:
							currentCasaAddress.address.cdBeneficiarioEnderecoFavorito,
						sequence:
							currentCasaAddress.address.sequence ??
							currentCasaAddress.address.cdBeneficiarioEnderecoFavorito,
						data: buildDowngradePayload(currentCasaAddress),
						existingLatLng: currentCasaAddress.address.cdLatitude
							? {
									latitude: currentCasaAddress.address.cdLatitude,
									longitude: currentCasaAddress.address.cdLongitude,
								}
							: undefined,
					});
				}

				await queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.PROFILE_FAVORITE_ADDRESSES],
				});
				navigate(ProfileEnum.FAVORITE_ADDRESSES_LIST, {
					state: {
						editSuccess: {
							addressLabel: formatAddress({
								address: finalData.address,
								number: finalData.number,
								district: finalData.district,
								city: finalData.city,
								state: finalData.state,
							}),
							isCasa: isCasaSelection,
						},
					},
				});
			} catch {
				handleOpenEditFavoriteAddressError();
			} finally {
				setIsSubmitting(false);
			}
		},
		[currentCasaAddress, currentItemSequence, queryClient, routeState],
	);

	const onSubmit = useCallback(
		(data: AddressFormV2Values) => {
			submitFavoriteAddress(data);
		},
		[submitFavoriteAddress],
	);

	const isMobOrTab = isMobile || isTablet;

	const item = routeState?.item
		? favoritePlaceToAddressFormParser({item: routeState.item})
		: null;

	return {
		isMobOrTab,
		handleBack,
		onSubmit,
		openEditFavoriteAddressError,
		handleCloseEditFavoriteAddressError,
		isPatchingFavoriteAddress: isSubmitting,
		item,
		isEditingCasaAddress,
		hasCasaAddress,
	};
}
